import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Papaparse from 'papaparse';
import { useContext, useState, useRef, useEffect } from 'react';
import { useLocalizeMessage, Table, Button, Spinner, useToastNotification, useDateTimeFormat } from 'libs.nucleus';
import { FileUtils } from 'libs.react';
import { bulkFetchPages, fetchReportData, fetchStudyData, sanitizeForCsv, toSentenceCaseWithSpaces } from './helpers';
import { useUserTrainingReportsTableColumn } from './user_training_reports_table_columns.hook';
import { ApiClientService, AuthContext, useApiClient } from '../../features';
export const UserTrainingReportsTable = ({ data, isLoading }) => {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const { columns } = useUserTrainingReportsTableColumn();
    const { addNotification } = useToastNotification();
    const { formatDateTime: formatDateTimeShort } = useDateTimeFormat();
    const momClient = useApiClient(ApiClientService.MOM);
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);
    const [isExportingCSV, setIsExportingCSV] = useState(false);
    const handleExportToCSV = async () => {
        abortControllerRef.current = new AbortController();
        setIsExportingCSV(true);
        try {
            const abortSignal = abortControllerRef.current.signal;
            const promises = [
                bulkFetchPages({
                    exec: (options) => fetchReportData({ apiClient: momClient, entityId, options }),
                    check: (currentResult, _, allResults) => currentResult.data.total <= allResults.length,
                    abortSignal,
                }),
                bulkFetchPages({
                    exec: (options) => fetchStudyData({ apiClient: momClient, entityId, options }),
                    check: (currentResult, page, allResults) => currentResult.data.total <= allResults.length || page >= 20, // Fetch studies up to 20 pages,
                    abortSignal,
                }),
            ];
            const [allReportsData, allStudiesData] = await Promise.all(promises);
            // Check if component is still mounted before proceeding
            if (!isMountedRef.current) {
                return;
            }
            const availableStudiesObj = allStudiesData.reduce((acc, study) => {
                acc[study.id.toString()] = study;
                return acc;
            }, {});
            const dataForCsv = allReportsData.map((item) => {
                const studyName = item.studyId ? availableStudiesObj[item.studyId]?.name : 'N/A';
                const mergedItem = {
                    ...item,
                    studyName,
                };
                const modifiedItemEntries = Object.entries(mergedItem).map(([key, value]) => [
                    toSentenceCaseWithSpaces(key),
                    ['completedOn'].includes(key)
                        ? value
                            ? formatDateTimeShort(value)
                            : ''
                        : sanitizeForCsv(value),
                ]);
                return Object.fromEntries(modifiedItemEntries);
            });
            const csvContent = Papaparse.unparse(dataForCsv, {
                header: true,
                skipEmptyLines: true,
                columns: columns.map((col) => col.header),
            });
            const timestamp = new Date().toISOString().split('T')[0];
            const fileName = `user-training-report-${timestamp}.csv`;
            FileUtils.downloadCsvFile(csvContent, fileName);
        }
        catch (_) {
            addNotification({ title: translate('Error occurred while exporting data to CSV.'), type: 'error' });
        }
        finally {
            setIsExportingCSV(false);
        }
    };
    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
        };
    }, []);
    return (_jsx(Table, { columns: columns, data: data || [], hasPagination: true, initialPageSize: 10, isLoading: isLoading, labelNoResults: translate('No training records found'), paginationNavigationScreenReaderLabel: translate('User reports table'), useInternalSearch: true, labelSearchField: translate('Search'), customHeaderContent: _jsx("div", { className: 'flex flex-row w-full justify-end', children: _jsx(Button, { label: translate('Export to CSV'), onClick: handleExportToCSV, disabled: !data?.length || isExportingCSV, content: isExportingCSV ? (_jsxs("div", { className: 'flex flex-row items-center gap-2', children: [_jsx(Spinner, { size: 'sm' }), _jsx("p", { className: 'text-sm text-gray-500 min-w-20 text-center', children: translate('Exporting') })] })) : undefined }) }) }));
};
